import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'

import { ClickWrapAdditionalActionsComponent } from '../../../clickwrap/types'
import * as Styled from '../styled'
import { CommunicatingEntities, CommunicatingEntity } from './CommunicatingEntities'

const entities: CommunicatingEntity[] = [
  {
    name: 'Designs for Health, Inc.',
    mailingAddress: '14 Commerce Blvd., Palm Coast, FL 32164',
    webAddress: (
      <Styled.WrappedLink to="https://www.designsforhealth.com" target="_blank">
        www.designsforhealth.com
      </Styled.WrappedLink>
    ),
  },
  {
    name: 'Designs for Health Ltd.',
    mailingAddress: '365 Bay Street, Suite 800, Toronto, ON M5H 2V1, Canada',
    webAddress: (
      <Styled.WrappedLink to="https://www.designsforhealth.ca" target="_blank">
        www.designsforhealth.ca
      </Styled.WrappedLink>
    ),
  },
  {
    name: 'Designs for Health Pty Ltd',
    mailingAddress: '1/418 Pittwater Rd., North Manly, NSW, 2100',
    webAddress: (
      <Styled.WrappedLink to="https://www.designsforhealth.com.au" target="_blank">
        www.designsforhealth.com.au
      </Styled.WrappedLink>
    ),
  },
  {
    name: 'MB360, LLC',
    mailingAddress: '14 Commerce Blvd., Palm Coast, FL 32164',
    webAddress: (
      <Styled.WrappedLink to="https://mbody360.io" target="_blank">
        Mbody360.io
      </Styled.WrappedLink>
    ),
  },
]

const RxDFHendCAContentActions: ClickWrapAdditionalActionsComponent<{
  className?: string
}> = ({ className, defaultChecked = false, disabled = true, onChanged, readonly = false }) => {
  const { register, watch } = useForm()
  const {
    AgeAcceptance,
    CommunicationsAcceptance,
    LiquidatedDamagesClauseAcceptance,
    PrivacyNoticeAcceptance,
    UnderstandRxDFHendAgreementAcceptance,
    UnsubscribeAtAnyTimeAcceptance,
  } = watch([
    'AgeAcceptance',
    'CommunicationsAcceptance',
    'LiquidatedDamagesClauseAcceptance',
    'PrivacyNoticeAcceptance',
    'UnderstandRxDFHendAgreementAcceptance',
    'UnsubscribeAtAnyTimeAcceptance',
  ])

  const isComplete =
    LiquidatedDamagesClauseAcceptance &&
    UnderstandRxDFHendAgreementAcceptance &&
    PrivacyNoticeAcceptance &&
    AgeAcceptance &&
    CommunicationsAcceptance &&
    UnsubscribeAtAnyTimeAcceptance

  useEffect(() => {
    onChanged({ isComplete })
  }, [isComplete, onChanged])

  return (
    <Styled.AdditionalActionsContainer className={className}>
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box, I signify that I have read the Liquidated Damages clause above,
            and I understand that I may be liable to Designs for Health to pay liquidated damages in
            the event of my breach of this Agreement.
          </Styled.CheckboxLabel>
        }
        id="LiquidatedDamagesClauseAcceptance"
        name="LiquidatedDamagesClauseAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box and clicking &quot;I Agree&quot; below, I signify that I have read
            and I understand this Agreement and that I, as the person or entity linked to this
            wholesale account, am legally bound by these terms.
          </Styled.CheckboxLabel>
        }
        id="UnderstandRxDFHendAgreementAcceptance"
        name="UnderstandRxDFHendAgreementAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box and clicking &quot;I Agree&quot; below, I signify that I have read
            and agree to DFH’s Privacy Notice, available at{' '}
            <a
              href="https://www.designsforhealth.com/privacy-notice"
              target="_blank"
              rel="noreferrer"
            >
              https://www.designsforhealth.com/privacy-notice
            </a>{' '}
            or such other URL as DFH may designate from time to time, which is incorporated herein
            by reference and forms part of this Agreement.
          </Styled.CheckboxLabel>
        }
        id="PrivacyNoticeAcceptance"
        name="PrivacyNoticeAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box and clicking &quot;I Agree&quot; below, I certify that I am at
            least the age of majority in the province/territory where I live.
          </Styled.CheckboxLabel>
        }
        id="AgeAcceptance"
        name="AgeAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box and clicking &quot;I Agree&quot; below, I agree to receive emails
            and text messages from the following entities about their products, services, and
            brands, my Account, and other issues related to my business relationship with DFH.
          </Styled.CheckboxLabel>
        }
        id="CommunicationsAcceptance"
        name="CommunicationsAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
        noGutterBottom
      />
      <Styled.CheckboxAside>
        <CommunicatingEntities entities={entities} />
      </Styled.CheckboxAside>
      <Styled.Checkbox
        label={
          <Styled.CheckboxLabel disabled={disabled}>
            By checking this box and clicking &quot;I Agree&quot; below, I acknowledge that I can
            unsubscribe at any time.
          </Styled.CheckboxLabel>
        }
        id="UnsubscribeAtAnyTimeAcceptance"
        name="UnsubscribeAtAnyTimeAcceptance"
        readonly={readonly}
        register={register}
        value=""
        disabled={disabled}
        defaultChecked={defaultChecked}
      />
    </Styled.AdditionalActionsContainer>
  )
}

const StyledRxDFHendCAContentActions = styled(RxDFHendCAContentActions)``

export default StyledRxDFHendCAContentActions
